import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import axios from 'axios';
import AddToCalendar from './AddToCalendar';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DoctorAppointmentsNew = () => {
  const { user: legacyUser } = useAuth();
  const { user: unifiedUser } = useUnifiedAuth();
  
  // Use unified auth if available, fallback to legacy
  const user = unifiedUser || legacyUser;
  
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState({
    upcoming: [],
    previous: [],
    cancelled: []
  });
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');

  useEffect(() => {
    fetchAppointments();
  }, [user]);

  const fetchAppointments = async () => {
    try {
      const token = localStorage.getItem('sessionToken');
      const response = await axios.get(`${API}/appointments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Split appointments into categories
      const now = new Date();
      const upcoming = [];
      const cancelled = [];
      const previous = [];
      
      response.data.forEach(apt => {
        const aptDate = new Date(`${apt.appointment_date}T${apt.appointment_time}`);
        
        if (apt.status === 'cancelled') {
          cancelled.push(apt);
        } else if (aptDate < now) {
          previous.push(apt);
        } else {
          upcoming.push(apt);
        }
      });
      
      // Sort each category
      upcoming.sort((a, b) => {
        const dateA = new Date(`${a.appointment_date}T${a.appointment_time}`);
        const dateB = new Date(`${b.appointment_date}T${b.appointment_time}`);
        return dateA - dateB;
      });
      
      previous.sort((a, b) => {
        const dateA = new Date(`${a.appointment_date}T${a.appointment_time}`);
        const dateB = new Date(`${b.appointment_date}T${b.appointment_time}`);
        return dateB - dateA;
      });
      
      cancelled.sort((a, b) => {
        const dateA = new Date(`${a.appointment_date}T${a.appointment_time}`);
        const dateB = new Date(`${b.appointment_date}T${b.appointment_time}`);
        return dateB - dateA;
      });
      
      setAppointments({
        upcoming,
        previous,
        cancelled
      });
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = (appointment) => {
    setSelectedAppointment(appointment);
    setCancellationReason('');
    setCancelModalOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedAppointment) return;
    
    setCancelling(true);
    try {
      const token = localStorage.getItem('sessionToken');
      await axios.put(
        `${API}/doctor/appointments/${selectedAppointment.id}/cancel?cancellation_reason=${encodeURIComponent(cancellationReason)}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      await fetchAppointments();
      setCancelModalOpen(false);
      setSelectedAppointment(null);
      setCancellationReason('');
      alert('Appointment cancelled successfully. User has been notified.');
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to cancel appointment.';
      alert(errorMsg);
    } finally {
      setCancelling(false);
    }
  };

  const handleRescheduleClick = (appointment) => {
    setSelectedAppointment(appointment);
    setNewDate(appointment.appointment_date);
    setNewTime(appointment.appointment_time);
    setRescheduleModalOpen(true);
  };

  const handleRescheduleConfirm = async () => {
    if (!selectedAppointment || !newDate || !newTime) {
      alert('Please select both date and time');
      return;
    }
    
    setRescheduling(true);
    try {
      const token = localStorage.getItem('sessionToken');
      await axios.put(
        `${API}/doctor/appointments/${selectedAppointment.id}/reschedule?new_date=${newDate}&new_time=${newTime}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      await fetchAppointments();
      setRescheduleModalOpen(false);
      setSelectedAppointment(null);
      setNewDate('');
      setNewTime('');
      alert('Appointment rescheduled successfully. User has been notified.');
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to reschedule appointment.';
      alert(errorMsg);
    } finally {
      setRescheduling(false);
    }
  };

  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 60; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    
    return dates;
  };

  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 20; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return slots;
  };

  const renderAppointmentCard = (appointment, isPrevious = false) => (
    <div key={appointment.id} className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h3 className="text-lg font-semibold text-gray-900">
              {appointment.patient_name || 'User'}
            </h3>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              appointment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
              appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {appointment.status}
            </span>
          </div>
          <div className="text-gray-600 space-y-1">
            <div className="flex items-center gap-2">
              <span>📅</span>
              <span>{appointment.appointment_date} at {appointment.appointment_time}</span>
            </div>
            {appointment.confirmation_number && (
              <div className="flex items-center gap-2">
                <span>🎫</span>
                <span className="font-mono text-sm">Confirmation: {appointment.confirmation_number}</span>
              </div>
            )}
            {appointment.service_name && (
              <div className="flex items-center gap-2">
                <span>⚕️</span>
                <span>Service: {appointment.service_name}</span>
              </div>
            )}
            {appointment.symptoms && (
              <div className="flex items-center gap-2">
                <span>💬</span>
                <span>Reason: {appointment.symptoms}</span>
              </div>
            )}
            {appointment.patient_phone && (
              <div className="flex items-center gap-2">
                <span>📞</span>
                <span>{appointment.patient_phone}</span>
              </div>
            )}
            {appointment.patient_email && (
              <div className="flex items-center gap-2">
                <span>✉️</span>
                <span>{appointment.patient_email}</span>
              </div>
            )}
            {appointment.consultation_fee && (
              <div className="flex items-center gap-2">
                <span>💰</span>
                <span>Fee: {appointment.consultation_fee} KD</span>
              </div>
            )}
          </div>
        </div>
        {!isPrevious && appointment.status === 'confirmed' && (
          <div className="flex flex-col gap-2">
            <AddToCalendar appointment={appointment} compact={true} />
            <button 
              className="bg-yellow-100 text-yellow-700 px-4 py-2 rounded hover:bg-yellow-200 transition-colors text-sm whitespace-nowrap"
              onClick={() => handleRescheduleClick(appointment)}
            >
              Reschedule
            </button>
            <button 
              className="bg-red-100 text-red-700 px-4 py-2 rounded hover:bg-red-200 transition-colors text-sm whitespace-nowrap"
              onClick={() => handleCancelClick(appointment)}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const hasAnyAppointments = appointments.upcoming.length > 0 || appointments.previous.length > 0 || appointments.cancelled.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Appointments</h1>
          <p className="text-gray-600 mt-2">Manage your scheduled user appointments</p>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading appointments...</p>
          </div>
        ) : !hasAnyAppointments ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📅</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">No appointments found</h2>
            <p className="text-gray-600">You don't have any appointments scheduled yet.</p>
          </div>
        ) : (
          <>
            {/* Upcoming Appointments */}
            {appointments.upcoming.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">📅 Upcoming Appointments ({appointments.upcoming.length})</h2>
                <div className="space-y-4">
                  {appointments.upcoming.map((appointment) => renderAppointmentCard(appointment, false))}
                </div>
              </div>
            )}

            {/* Previous Appointments */}
            {appointments.previous.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-700 mb-4">📋 Previous Appointments ({appointments.previous.length})</h2>
                <div className="space-y-4">
                  {appointments.previous.map((appointment) => renderAppointmentCard(appointment, true))}
                </div>
              </div>
            )}

            {/* Cancelled Appointments */}
            {appointments.cancelled.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-500 mb-4">❌ Cancelled Appointments ({appointments.cancelled.length})</h2>
                <div className="space-y-4">
                  {appointments.cancelled.map((appointment) => renderAppointmentCard(appointment, false))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Cancel Modal */}
        {cancelModalOpen && selectedAppointment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Cancel Appointment</h3>
              <p className="text-gray-600 mb-2">
                Are you sure you want to cancel this appointment?
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="font-semibold text-gray-900">{selectedAppointment.patient_name}</p>
                <p className="text-sm text-gray-600">
                  {selectedAppointment.appointment_date} at {selectedAppointment.appointment_time}
                </p>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cancellation Reason (will be sent to patient)
                </label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="e.g., Emergency, Illness, Schedule conflict..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>
              
              <p className="text-sm text-blue-600 mb-6">
                ℹ️ Patient will be notified immediately via notification, email, and SMS.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setCancelModalOpen(false);
                    setSelectedAppointment(null);
                    setCancellationReason('');
                  }}
                  disabled={cancelling}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Keep Appointment
                </button>
                <button
                  onClick={handleCancelConfirm}
                  disabled={cancelling}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reschedule Modal */}
        {rescheduleModalOpen && selectedAppointment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Reschedule Appointment</h3>
              <p className="text-gray-600 mb-2">
                Rescheduling appointment for:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="font-semibold text-gray-900">{selectedAppointment.patient_name}</p>
                <p className="text-sm text-gray-600">
                  Current: {selectedAppointment.appointment_date} at {selectedAppointment.appointment_time}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Date
                  </label>
                  <select
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a date</option>
                    {getAvailableDates().map(date => (
                      <option key={date} value={date}>
                        {new Date(date).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Time
                  </label>
                  <select
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a time</option>
                    {getTimeSlots().map(time => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <p className="text-sm text-blue-600 mb-6">
                ℹ️ Patient will be notified of the new time via notification, email, and SMS.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setRescheduleModalOpen(false);
                    setSelectedAppointment(null);
                    setNewDate('');
                    setNewTime('');
                  }}
                  disabled={rescheduling}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRescheduleConfirm}
                  disabled={rescheduling || !newDate || !newTime}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {rescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorAppointmentsNew;
